// src/PayoutsPage.js - Admin payout requests management
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  MenuItem,
  FormControl,
  Select,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import Avatar from '@mui/material/Avatar';
import Grid from '@mui/material/Grid';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { supabase } from './supabaseClient';

import { brand, ink, status, statusTone, surface } from './designTokens';
import { prettyStatus } from './labels';
export default function PayoutsPage() {
  const [payouts, setPayouts] = useState([]);
  const [allPayouts, setAllPayouts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPayout, setSelectedPayout] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [action, setAction] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [refundSearch, setRefundSearch] = useState('');
  const [refundSearchLoading, setRefundSearchLoading] = useState(false);
  const [refundSearchError, setRefundSearchError] = useState(null);
  const [refundResults, setRefundResults] = useState(null);
  const [refundActionLoading, setRefundActionLoading] = useState(null);
  const [stats, setStats] = useState({
    totalPaid: 0,
    pendingPayouts: 0,
    processedCount: 0,
    pendingCount: 0,
  });
  const [filterEligibility, setFilterEligibility] = useState('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch payouts without foreign key join to avoid RLS recursion
      const { data, error } = await supabase
        .from('payout_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!data || data.length === 0) {
        setPayouts([]);
        setAllPayouts([]);
        setLoading(false);
        return;
      }

      // Fetch stylist profiles separately
      const stylistPhones = [...new Set(data.map(p => p.stylist_phone_number).filter(Boolean))];
      const stylistMap = new Map();
      if (stylistPhones.length > 0) {
        const { data: stylists } = await supabase
          .from('Profiles')
          .select('id, first_name, last_name, phone_number, email, profile_image')
          .in('phone_number', stylistPhones);
        
        if (stylists) {
          stylists.forEach(s => stylistMap.set(s.phone_number, s));
        }
      }

      // Fetch latest legal case per stylist (for payout pause/resume actions)
      const stylistIds = [...new Set(Array.from(stylistMap.values()).map(s => s?.id).filter(Boolean))];
      const legalMap = {};
      const compMap = {};
      if (stylistIds.length > 0) {
        const { data: legalRows } = await supabase
          .from('stylist_agreements')
          .select('id, stylist_id, status, signed_at, agreement_version')
          .in('stylist_id', stylistIds)
          .order('created_at', { ascending: false });

        (legalRows || []).forEach((row) => {
          if (!legalMap[row.stylist_id]) legalMap[row.stylist_id] = row;
        });

        const { data: compRows } = await supabase
          .from('stylist_compliance_status')
          .select('stylist_id, agreement_status, identity_status, payout_status')
          .in('stylist_id', stylistIds);
        
        (compRows || []).forEach(row => {
          compMap[row.stylist_id] = row;
        });
      }

      // Fetch payout methods separately
      const payoutMethodIds = [...new Set(data.map(p => p.payout_method_id).filter(Boolean))];
      const methodMap = new Map();
      if (payoutMethodIds.length > 0) {
        const { data: methods } = await supabase
          .from('stylist_payout_methods')
          .select('id, method_type, provider, bank_name, account_last4, card_brand, card_last4, stripe_payout_method_id')
          .in('id', payoutMethodIds);
        
        if (methods) {
          methods.forEach(m => methodMap.set(m.id, m));
        }
      }

      // Enrich payouts with profile and method data
      const enriched = (data || []).map(payout => {
        const stylistProfile = payout.stylist_phone_number ? stylistMap.get(payout.stylist_phone_number) : null;
        const stylistId = stylistProfile?.id || null;
        const latestLegalCase = stylistId ? legalMap[stylistId] || null : null;
        const compStatus = stylistId ? compMap[stylistId] || {} : {};
        const isEligible = compStatus.payout_status === 'eligible';
        return {
          ...payout,
          stylist: stylistProfile ? [stylistProfile] : null,
          stylist_id: stylistId,
          legal_case_id: latestLegalCase?.id || null,
          compliance: compStatus,
          is_eligible: isEligible,
          payout_method: payout.payout_method_id ? [methodMap.get(payout.payout_method_id)] : null,
        };
      });

      setPayouts(enriched);
      setAllPayouts(enriched);

      // Calculate stats
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const totalPaid = enriched
        .filter(p => p.status === 'completed' && new Date(p.processed_at || p.created_at) >= firstDayOfMonth)
        .reduce((sum, p) => sum + (parseFloat(p.total_amount || p.amount || 0)), 0);

      const pendingPayouts = enriched
        .filter(p => p.status === 'pending')
        .reduce((sum, p) => sum + (parseFloat(p.total_amount || p.amount || 0)), 0);

      const pendingCount = enriched.filter(p => p.status === 'pending').length;

      const processedCount = enriched
        .filter(p => p.status === 'completed' && new Date(p.processed_at || p.created_at) >= firstDayOfMonth)
        .length;

      setStats({
        totalPaid,
        pendingPayouts,
        processedCount,
        pendingCount,
      });
    } catch (error) {
      console.error('Error fetching payouts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter and search
  useEffect(() => {
    let filtered = [...allPayouts];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(payout => {
        const stylist = payout.stylist?.[0] || {};
        const matchesName = `${stylist.first_name || ''} ${stylist.last_name || ''}`.toLowerCase().includes(term);
        const matchesPhone = payout.stylist_phone_number?.toLowerCase().includes(term);
        const matchesEmail = stylist.email?.toLowerCase().includes(term);
        const matchesAmount = payout.total_amount?.toString().includes(term);
        return matchesName || matchesPhone || matchesEmail || matchesAmount;
      });
    }

    if (filterEligibility !== 'all') {
      filtered = filtered.filter(p => {
        if (filterEligibility === 'eligible') return p.is_eligible;
        if (filterEligibility === 'blocked') return !p.is_eligible;
        return true;
      });
    }

    setPayouts(filtered);
    setPage(1); // filters changed the result set — the old page number may no longer exist
  }, [searchTerm, filterEligibility, allPayouts]);

  const totalPages = Math.max(1, Math.ceil(payouts.length / PAGE_SIZE));
  const pagedPayouts = payouts.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  // Up to 5 page numbers centered on the current page, clamped to the real range.
  const pageNumbers = Array.from(
    { length: Math.min(5, totalPages) },
    (_, i) => Math.min(Math.max(page - 2, 1), Math.max(totalPages - 4, 1)) + i
  );

  useEffect(() => {
    fetchData();

    // Live-update when a payout request is submitted/approved/rejected
    // elsewhere instead of requiring a manual refresh.
    const channel = supabase
      .channel('payouts-page-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'payout_requests' }, () => {
        fetchData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const handleOpenDialog = (payout, actionType = 'view') => {
    setSelectedPayout(payout);
    setAction(actionType);
    setAdminNotes(payout?.admin_notes || '');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPayout(null);
    setAction(null);
    setAdminNotes('');
  };

  const handleProcessPayout = async (nextAction = action) => {
    if (!selectedPayout || !nextAction || nextAction === 'view') return;

    setProcessing(true);
    try {
      // The edge function is the sole writer of payout_requests.status for
      // both actions: it only proceeds while status is still "pending", and
      // for approvals it's what actually moves the money (a real Stripe
      // Transfer or Plaid transfer) and reconciles the stylist_earnings
      // rows this payout covers (processing -> paid on success, back to
      // available on reject, so rejected money isn't stuck forever).
      //
      // This used to update payout_requests directly first (status:
      // 'approved'/'rejected') and only call the function afterward for
      // approvals — with mismatched field/action names on top
      // (payout_request_id + action:'process' vs. the function's
      // request_id + action:'approve'). Combined, that meant: rejects never
      // reached the function at all (earnings never reverted), and
      // approvals always failed both the name mismatch and the function's
      // own "must still be pending" check (since this already flipped it to
      // 'approved' beforehand) — so no approval could ever actually
      // execute a transfer. Routing both actions through the function, with
      // no direct write first, is what the function's contract expects.
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/process-payout-request`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            request_id: selectedPayout.id,
            action: nextAction, // 'approve' | 'reject'
            admin_notes: adminNotes || null,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to process payout');
      }

      await fetchData();
      handleCloseDialog();
      alert(`Payout ${nextAction === 'approve' ? 'approved' : 'rejected'} successfully`);
    } catch (error) {
      console.error('Error processing payout:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };


  const handlePayoutGateAction = async (payout, pause = true) => {
    try {
      const stylistId = payout?.stylist?.[0]?.id;
      if (!stylistId) throw new Error('Missing stylist id for payout compliance action');

      let caseId = payout?.legal_case_id;
      if (!caseId) {
        const { data: latestCase, error: caseErr } = await supabase
          .from('stylist_agreements')
          .select('id')
          .eq('stylist_id', stylistId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (caseErr) throw caseErr;
        caseId = latestCase?.id;
      }

      if (!caseId) throw new Error('No legal agreement row exists for this stylist. Run legal onboarding first.');

      const action = pause ? 'pause_payout' : 'resume_payout';
      const { error } = await supabase.functions.invoke('admin-legal-bulk-action', {
        body: { action, case_ids: [caseId] },
      });
      if (error) throw error;

      // Pausing only sets the compliance flag (blocks future payouts for
      // this stylist without touching a request already in flight).
      // Resuming, though, needs to actually do something to the request
      // sitting right here — clearing the gate alone left the row
      // permanently "pending" with nothing else able to move it forward.
      // So resume also runs the same approve path as the "Approve" button
      // (process-payout-request): the real Stripe/Plaid transfer, plus
      // reconciling the covered stylist_earnings rows from processing to
      // paid — via the same edge function, not a separate code path.
      if (!pause) {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(
          `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/process-payout-request`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({
              request_id: payout.id,
              action: 'approve',
              admin_notes: 'Resumed from Payouts page after clearing compliance hold',
            }),
          }
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Compliance hold cleared, but the payout itself failed to process');
        }
        alert('Payout resumed and processed successfully.');
      } else {
        alert('Payout paused for stylist successfully.');
      }
      await fetchData();
    } catch (error) {
      console.error('Error updating payout gate:', error);
      // supabase-js's FunctionsHttpError.message is just a generic "non-2xx"
      // string — the real reason is in the response body, reachable via
      // error.context (the raw Response object).
      let detail = error.message || 'Failed to update payout gate';
      try {
        const body = await error.context?.json();
        if (body?.error) detail = body.error;
      } catch (_) {
        // context wasn't JSON or wasn't readable — fall back to error.message
      }
      alert(`Error: ${detail}`);
    }
  };

  // Client refunds — separate from the stylist-payouts table above, but
  // lives on this same screen so admins have one place for both sides of
  // money movement on a booking.
  const handleRefundSearch = async () => {
    if (!refundSearch.trim()) return;
    setRefundSearchLoading(true);
    setRefundSearchError(null);
    setRefundResults(null);
    try {
      const term = refundSearch.trim();
      const { data: clients, error: clientError } = await supabase
        .from('Profiles')
        .select('id, first_name, last_name, phone_number, email')
        .or(`phone_number.ilike.%${term}%,email.ilike.%${term}%,first_name.ilike.%${term}%,last_name.ilike.%${term}%`)
        .limit(5);
      if (clientError) throw clientError;

      if (!clients || clients.length === 0) {
        setRefundResults({ client: null, bookings: [] });
        return;
      }

      const client = clients[0];
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('id, price, currency, status, payment_completed, start_ts, service_title')
        .eq('client_id', client.id)
        .eq('payment_completed', true)
        .order('start_ts', { ascending: false });
      if (bookingsError) throw bookingsError;

      setRefundResults({ client, bookings: bookings || [] });
    } catch (error) {
      console.error('Error searching for client:', error);
      setRefundSearchError(error.message || 'Failed to search for client');
    } finally {
      setRefundSearchLoading(false);
    }
  };

  const handleIssueRefund = async (bookingId) => {
    if (!window.confirm('Issue a full refund for this booking? This cannot be undone.')) return;
    setRefundActionLoading(bookingId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/issue-refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ booking_id: bookingId, reason: 'requested_by_customer' }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to issue refund');
      }
      await handleRefundSearch();
    } catch (error) {
      console.error('Error issuing refund:', error);
      alert(`Error: ${error.message || 'Failed to issue refund'}`);
    } finally {
      setRefundActionLoading(null);
    }
  };

  const getPayoutMethodDisplay = (method) => {
    if (!method || !method[0]) return 'N/A';
    const m = method[0];
    // This is actually the common case now — every stylist who's completed
    // Stripe Connect onboarding has method_type 'stripe_connect' /
    // provider 'stripe', which matched none of the branches below and fell
    // through to "Card ••••" with no last4 (stripe_connect rows don't have
    // card_brand/card_last4 at all — they use stripe_payout_method_id,
    // the acct_... id). Handle it explicitly instead of silently
    // mislabeling the one payout method type this console actually needs
    // to show correctly.
    if (m.method_type === 'stripe_connect') {
      return `Stripe Connect${m.stripe_payout_method_id ? ` (${m.stripe_payout_method_id})` : ''}`;
    }
    if (m.method_type === 'bank_account' || m.provider === 'bank_transfer') {
      return `Bank Transfer`;
    }
    if (m.provider === 'paypal') {
      return 'PayPal';
    }
    return `${m.card_brand || 'Card'} ••••${m.card_last4 || ''}`;
  };

  // Delegates to the shared statusTone() so every screen renders the same
  // status identically. (The parameter was named `status`, which shadowed the
  // imported token of the same name — every colour here resolved to undefined.)
  const getStatusChipColor = (value) => {
    const tone = statusTone(value);
    return { bg: tone.bg, color: tone.fg };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const getStylistImageUrl = (stylist) => {
    if (!stylist) return null;
    if (stylist.profile_image_url) return stylist.profile_image_url;
    if (stylist.profile_image) {
      const { data } = supabase.storage
        .from('profile-images')
        .getPublicUrl(`profiles/${stylist.profile_image}`);
      return data.publicUrl;
    }
    return null;
  };

  return (
    <Box sx={{ p: 4, bgcolor: surface.page, minHeight: '100vh' }}>
      {/* Overview Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 3,
              border: `1px solid ${surface.line}`
            }}
          >
            <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
              Total Paid (This Month)
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              ${stats.totalPaid.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="caption" sx={{ color: status.success, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ArrowUpwardIcon sx={{ fontSize: 12 }} />
              5.2% vs last month
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 3,
              border: `1px solid ${surface.line}`
            }}
          >
            <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
              Pending Payouts
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              ${stats.pendingPayouts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
            <Typography variant="caption" sx={{ color: ink[500] }}>
              {stats.pendingCount} stylists awaiting payment
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2.5, 
              borderRadius: 3,
              border: `1px solid ${surface.line}`
            }}
          >
            <Typography variant="body2" sx={{ color: ink[500], mb: 0.5 }}>
              Payouts Processed
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {stats.processedCount}
            </Typography>
            <Typography variant="caption" sx={{ color: ink[500] }}>
              In the last 30 days
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Client Refunds Section */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${surface.line}`, p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Issue a Client Refund
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, mb: refundResults ? 2 : 0 }}>
          <TextField
            placeholder="Search client by name, phone, or email…"
            size="small"
            fullWidth
            value={refundSearch}
            onChange={(e) => setRefundSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRefundSearch()}
          />
          <Button
            variant="contained"
            disabled={refundSearchLoading || !refundSearch.trim()}
            onClick={handleRefundSearch}
            sx={{ bgcolor: ink[900], textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: ink[900] } }}
          >
            {refundSearchLoading ? 'Searching…' : 'Search'}
          </Button>
        </Box>

        {refundSearchError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRefundSearchError(null)}>{refundSearchError}</Alert>
        )}

        {refundResults && (
          !refundResults.client ? (
            <Typography color="textSecondary">No client found matching "{refundSearch}".</Typography>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <strong>{`${refundResults.client.first_name || ''} ${refundResults.client.last_name || ''}`.trim() || 'Unknown'}</strong>
                {' — '}{refundResults.client.phone_number}{refundResults.client.email ? ` · ${refundResults.client.email}` : ''}
              </Typography>
              {refundResults.bookings.length === 0 ? (
                <Typography color="textSecondary">No paid bookings found for this client.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Service</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {refundResults.bookings.map((b) => (
                        <TableRow key={b.id} hover>
                          <TableCell>{b.service_title || 'N/A'}</TableCell>
                          <TableCell>{b.start_ts ? new Date(b.start_ts).toLocaleDateString() : '-'}</TableCell>
                          <TableCell>${parseFloat(b.price || 0).toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={prettyStatus(b.status)}
                              size="small"
                              color={b.status === 'Refunded' ? 'default' : b.status === 'Completed' ? 'success' : 'warning'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            {b.status === 'Refunded' ? (
                              <Typography variant="caption" color="textSecondary">Refunded</Typography>
                            ) : (
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                disabled={refundActionLoading === b.id}
                                onClick={() => handleIssueRefund(b.id)}
                              >
                                {refundActionLoading === b.id ? 'Refunding…' : 'Issue Refund'}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )
        )}
      </Paper>

      {/* Payouts Table Section */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${surface.line}`,
          overflow: 'hidden'
        }}
      >
        {/* Table Header */}
        <Box 
          sx={{ 
            p: 3, 
            borderBottom: `1px solid ${surface.line}`,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Payout History
            </Typography>
            <Chip label="Live" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Box sx={{ position: 'relative' }}>
              <SearchIcon 
                sx={{ 
                  position: 'absolute', 
                  left: 12, 
                  top: '50%', 
                  transform: 'translateY(-50%)',
                  color: ink[300],
                  fontSize: 20
                }} 
              />
              <TextField
                placeholder="Search by stylist..."
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  width: 256,
                  '& .MuiOutlinedInput-root': {
                    pl: 4.5,
                    '& fieldset': {
                      borderColor: surface.line,
                    },
                  },
                }}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 150, bgcolor: 'white' }}>
              <Select
                value={filterEligibility}
                onChange={(e) => setFilterEligibility(e.target.value)}
                displayEmpty
                sx={{ '& .MuiSelect-select': { py: 1 } }}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="eligible">Eligible Only</MenuItem>
                <MenuItem value="blocked">Blocked / Paused</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              sx={{
                borderColor: surface.line,
                color: ink[500],
                bgcolor: surface.page,
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { borderColor: ink[300], bgcolor: surface.sunken }
              }}
            >
              Filter
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                bgcolor: ink[900],
                color: 'white',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': { bgcolor: ink[900] }
              }}
            >
              New Payout
            </Button>
          </Box>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: surface.page }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Stylist
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Payout ID
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Date
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Amount
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Method
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Compliance
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: ink[700] }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: ink[500] }}>
                    No payout requests found
                  </TableCell>
                </TableRow>
              ) : (
                pagedPayouts.map((payout) => {
                  const stylist = payout.stylist?.[0] || {};
                  const stylistImageUrl = getStylistImageUrl(stylist);
                  const statusColors = getStatusChipColor(payout.status);
                  
                  return (
                    <TableRow key={payout.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar
                            src={stylistImageUrl}
                            sx={{ width: 32, height: 32 }}
                          >
                            {stylist.first_name?.[0] || ''}{stylist.last_name?.[0] || ''}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {stylist.first_name} {stylist.last_name || ''}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', color: ink[700] }}>
                        #{payout.id?.slice(0, 16).toUpperCase() || 'N/A'}
                      </TableCell>
                      <TableCell>
                        {payout.created_at
                          ? new Date(payout.created_at).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })
                          : '-'}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        ${parseFloat(payout.total_amount || payout.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell>{getPayoutMethodDisplay(payout.payout_method)}</TableCell>
                      <TableCell>
                        <Chip
                          label={prettyStatus(payout.status) || 'Pending'}
                          size="small"
                          sx={{
                            bgcolor: statusColors.bg,
                            color: statusColors.color,
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            height: 20
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {payout.is_eligible ? (
                            <Chip
                              label="Eligible"
                              size="small"
                              sx={{ bgcolor: status.successBg, color: status.success, fontWeight: 600, fontSize: '0.7rem', height: 20, width: 'fit-content' }}
                            />
                          ) : (
                            <Chip
                              label={payout.compliance?.payout_status === 'paused' ? 'Paused' : 'Blocked'}
                              size="small"
                              sx={{ bgcolor: status.dangerBg, color: status.danger, fontWeight: 600, fontSize: '0.7rem', height: 20, width: 'fit-content' }}
                            />
                          )}
                          {payout.compliance?.agreement_status !== 'signed' && (
                            <Typography variant="caption" color="error" sx={{ fontSize: '0.65rem' }}>
                              Missing Agreement
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            sx={{
                              color: brand.gold,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'none',
                              '&:hover': { color: brand.goldDeep, bgcolor: 'transparent' }
                            }}
                            onClick={() => handleOpenDialog(payout, 'view')}
                          >
                            View Details
                          </Button>
                          {/* Pause/Resume are a stylist-wide compliance gate
                              (stylist_compliance_status.payout_status), not
                              an action on this specific request — they only
                              make sense while a request is still pending.
                              Once approved/completed the money has already
                              moved, and once rejected/failed the request is
                              dead (process-payout-request refuses to act on
                              anything but a pending request), so showing
                              them there implied an effect they don't have. */}
                          {payout.status?.toLowerCase() === 'pending' && (
                            <>
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                onClick={() => handlePayoutGateAction(payout, true)}
                              >
                                Pause Payout
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                onClick={() => handlePayoutGateAction(payout, false)}
                              >
                                Resume Payout
                              </Button>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {payouts.length > 0 && (
          <Box 
            sx={{ 
              p: 2, 
              borderTop: `1px solid ${surface.line}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="body2" sx={{ color: ink[500] }}>
              Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, payouts.length)} of {payouts.length} results
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                sx={{
                  minWidth: 32,
                  color: ink[500],
                  border: `1px solid ${surface.line}`,
                  '&:disabled': { opacity: 0.5 }
                }}
                disabled={page === 1}
                onClick={() => setPage(p => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              {pageNumbers[0] > 1 && (
                <Typography sx={{ px: 1, color: ink[500] }}>...</Typography>
              )}
              {pageNumbers.map(n => (
                <Button
                  key={n}
                  size="small"
                  onClick={() => setPage(n)}
                  sx={n === page
                    ? { minWidth: 32, bgcolor: ink[900], color: 'white' }
                    : { minWidth: 32, color: ink[700], border: `1px solid ${surface.line}` }}
                >
                  {n}
                </Button>
              ))}
              {pageNumbers[pageNumbers.length - 1] < totalPages && (
                <Typography sx={{ px: 1, color: ink[500] }}>...</Typography>
              )}
              <Button
                size="small"
                sx={{
                  minWidth: 32,
                  color: ink[700],
                  border: `1px solid ${surface.line}`,
                  '&:disabled': { opacity: 0.5 }
                }}
                disabled={page === totalPages}
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Process Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {action === 'approve' ? 'Approve Payout Request' : action === 'reject' ? 'Reject Payout Request' : 'Payout Request Details'}
        </DialogTitle>
        <DialogContent>
          {selectedPayout && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Stylist: {selectedPayout.stylist?.[0]?.first_name}{' '}
                {selectedPayout.stylist?.[0]?.last_name}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Amount: ${parseFloat(selectedPayout.total_amount || selectedPayout.amount || 0).toFixed(2)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Method: {getPayoutMethodDisplay(selectedPayout.payout_method)}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Admin Notes"
            value={adminNotes}
            onChange={(e) => setAdminNotes(e.target.value)}
            placeholder={
              action === 'approve'
                ? 'Add notes about this approval...'
                : action === 'reject'
                ? 'Reason for rejection (required)'
                : 'Internal admin notes...'
            }
            required={action === 'reject'}
            sx={{ mt: 2 }}
          />
          {action === 'reject' && !adminNotes && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please provide a reason for rejection
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={processing}>
            Close
          </Button>

          {selectedPayout?.status === 'pending' && (
            <>
              <Button
                onClick={() => {
                  setAction('reject');
                  handleProcessPayout('reject');
                }}
                variant="contained"
                color="error"
                disabled={processing || !adminNotes}
              >
                {processing && action === 'reject' ? 'Processing...' : 'Reject'}
              </Button>
              <Button
                onClick={() => {
                  setAction('approve');
                  handleProcessPayout('approve');
                }}
                variant="contained"
                color="success"
                disabled={processing}
              >
                {processing && action === 'approve' ? 'Processing...' : 'Approve'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

